// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import { mergeWithCss } from "@werner/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface SignInButtonStyles {
    button?: ButtonPresentationProps;
    link?: LinkPresentationProps;
}
interface OwnProps {
    labelOverride?: string;
    extendedStyles?: SignInButtonStyles;
    type?: "button" | "link";
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    currentLocation: getLocation(state),
    isAuthenticated: xIsAuthenticated(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const signInButtonStyles: SignInButtonStyles = {
    button: {
        variant: "primary",
        mergeCss: true,
        css: css`
            width: 100%;
        `,
    },
};

const SignInButton: FC<Props> = ({
    labelOverride,
    isAuthenticated,
    signInUrl,
    currentUserIsGuest,
    userName,
    currentLocation,
    isSignInPage,
    extendedStyles,
    type,
}) => {
    const [styles] = useState(() => mergeWithCss(signInButtonStyles, extendedStyles));
    const history = useHistory();
    if (isAuthenticated) {
        return null;
    }

    const onSignInHandler = () => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    if (type === "link") {
        return (
            <Link
                {...styles.link}
                aria-label={labelOverride || translate("Sign In For Price")}
                onClick={onSignInHandler}
            >
                {labelOverride || translate("Sign In For Price")}
            </Link>
        );
    }

    return (
        <Button
            onClick={onSignInHandler}
            {...styles.button}
            aria-label={labelOverride || translate("Sign In For Price")}
        >
            {labelOverride || translate("Sign In For Price")}
        </Button>
    );
};

export default connect(mapStateToProps)(SignInButton);
